import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SEO_PAGE_CONFIG } from "@/config/seoPages";
import { SEO_PAGE_RU_CONTENT } from "@/config/seoPagesRu";
import { PRIVACY_POLICY } from "@/config/privacyPolicy";
import { useLang } from "@/i18n/LangContext";

const BASE_URL = "https://melnikbrokers.ae";
const PUBLIC_TITLE = "MELNIK Commercial Brokers | B2B Commercial Introductions in Dubai";
const PUBLIC_DESCRIPTION = "MELNIK Commercial Brokers in Dubai coordinates B2B commercial introductions for qualified companies, with dedicated gold, petroleum, KYC/KYB, AML, SBLC and bank-to-bank workflow directions.";
const PUBLIC_ROBOTS = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const PRIVATE_ROBOTS = "noindex, nofollow, noarchive, nosnippet";

const setMeta = (selector, content) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    const identity = selector.match(/^meta\[(name|property)="([^"]+)"\]$/);
    if (identity) element.setAttribute(identity[1], identity[2]);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

const setStructuredData = (page, canonical) => {
  let script = document.getElementById("route-seo-schema");
  if (!script) {
    script = document.createElement("script");
    script.id = "route-seo-schema";
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  const organization = {
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`,
    name: "MELNIK Commercial Brokers",
    url: `${BASE_URL}/`,
    email: "info@melnikbrokers.ae",
    address: { "@type": "PostalAddress", addressLocality: "Dubai", addressCountry: "AE" }
  };

  const baseSchema = {
    "@context": "https://schema.org",
    "@graph": [
      organization,
      {
        "@type": "WebPage",
        "@id": canonical,
        url: canonical,
        name: page?.title || PUBLIC_TITLE,
        description: page?.metaDescription || PUBLIC_DESCRIPTION,
        isPartOf: { "@type": "WebSite", url: `${BASE_URL}/`, name: "MELNIK Commercial Brokers" },
        about: page?.isPrivacyPolicy ? ["Privacy policy", "Personal data", "Meta lead forms"] : page ? ["Gold brokerage", "Wholesale gold enquiries", "LBMA reference price discussions", "SBLC coordination", "Commercial introductions", "KYC/KYB", "AML"] : ["Gold brokerage", "Petroleum products", "Commercial brokerage"]
      }
    ]
  };

  if (page && !page.isPrivacyPolicy && Array.isArray(page.faqs)) {
    baseSchema["@graph"].push(
      {
        "@type": "Service",
        name: page.title,
        description: page.metaDescription,
        provider: { "@id": `${BASE_URL}/#organization` },
        areaServed: { "@type": "Place", name: "Dubai, United Arab Emirates" },
        url: canonical
      },
      {
        "@type": "FAQPage",
        mainEntity: page.faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer }
        }))
      }
    );
  }

  script.textContent = JSON.stringify(baseSchema);
};

const removeRouteSchema = () => {
  document.getElementById("route-seo-schema")?.remove();
};

const removeCanonical = () => {
  document.head.querySelector('link[rel="canonical"]')?.remove();
};

export const RouteSeo = () => {
  const { pathname } = useLocation();
  const { lang } = useLang();

  useEffect(() => {
    const isPrivateRoute = pathname === "/desk-047" || pathname.startsWith("/client-information/");
    const privacyPage = pathname === PRIVACY_POLICY.route ? {
      title: lang === "ru" ? PRIVACY_POLICY.ru.title : PRIVACY_POLICY.en.title,
      metaTitle: lang === "ru" ? "Политика конфиденциальности | MELNIK Commercial Brokers" : PRIVACY_POLICY.metaTitle,
      metaDescription: lang === "ru" ? "Политика конфиденциальности MELNIK Commercial Brokers для заявок через сайт и формы Meta, Facebook и Instagram." : PRIVACY_POLICY.metaDescription,
      isPrivacyPolicy: true
    } : null;
    const page = privacyPage || (lang === "ru" ? SEO_PAGE_RU_CONTENT : SEO_PAGE_CONFIG)[pathname];
    const canonical = `${BASE_URL}${pathname === "/" ? "/" : pathname}`;
    const title = isPrivateRoute ? "Secure Access | MELNIK Commercial Brokers" : (page?.metaTitle || PUBLIC_TITLE);
    const description = page?.metaDescription || PUBLIC_DESCRIPTION;

    document.title = title;
    setMeta('meta[name="robots"]', isPrivateRoute ? PRIVATE_ROBOTS : PUBLIC_ROBOTS);
    setMeta('meta[name="description"]', isPrivateRoute ? "" : description);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', isPrivateRoute ? "" : description);
    setMeta('meta[property="og:url"]', isPrivateRoute ? "" : canonical);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', isPrivateRoute ? "" : description);

    if (isPrivateRoute) {
      removeCanonical();
      removeRouteSchema();
    } else {
      setLink("canonical", canonical);
      setStructuredData(page, canonical);
    }
  }, [lang, pathname]);

  return null;
};
